import { Edit } from '@tunasong/icons'
import type { FC } from 'react'
import { Tooltip } from '../primitives/tooltip.js'
import styles from './sketch-badge.module.css'

/**
 * Marks generated media as scaffolding — something to work from or replace, not the
 * finished work.
 *
 * The visible word is "Sketch", never "AI". Musicians told us in research that an AI
 * label on work they keep devalues it, so the badge names what the thing IS (a
 * placeholder) rather than what produced it. Provenance is not hidden — it moves to the
 * tooltip and the aria-label, where someone asking "where did this come from?" finds a
 * straight answer.
 *
 * The icon is a pencil for the same reason: a sketch is a draft, and brain/robot/sparkle
 * iconography brands the artifact as machine-made at a glance.
 */
export type SketchBadgeSize = 'xs' | 'sm' | 'md'

export interface SketchBadgeProps {
  /** Defaults to 'sm'. xs = icon-only, sm and md = icon + 'Sketch'. */
  size?: SketchBadgeSize
  /** Render with the dark overlay backdrop — for placement over images / audio. */
  overlay?: boolean
  /** Override className for layout positioning (e.g. absolute overlay placement). */
  className?: string
}

/** Provenance lives here rather than on the badge face — discoverable, not branded. */
const ariaLabel = 'Sketch generated by Tunabrain — replace with your own'
const tooltipTitle = 'Sketch generated by Tunabrain — replace with your own'

const iconPxBySize: Record<SketchBadgeSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
}

const visibleLabelBySize: Record<SketchBadgeSize, string | null> = {
  xs: null,
  sm: 'Sketch',
  md: 'Sketch',
}

export const SketchBadge: FC<SketchBadgeProps> = ({ size = 'sm', overlay = false, className }) => {
  const label = visibleLabelBySize[size]
  const badge = (
    <span
      role="img"
      aria-label={ariaLabel}
      data-size={size}
      data-overlay={overlay ? 'true' : undefined}
      className={[styles.badge, className].filter(Boolean).join(' ')}
    >
      <Edit size={iconPxBySize[size]} aria-hidden={true} />
      {label ? <span className={styles.label}>{label}</span> : null}
    </span>
  )
  return <Tooltip title={tooltipTitle}>{badge}</Tooltip>
}

export default SketchBadge
